import React, { useEffect, useState } from 'react';
import { UserProfile, Study, Organization, StudyMetrics, Course, CourseMetrics, VisibilityLevel } from '../types';
import { Icons } from './ui/Icons';
import { getOrgStudies, getUserStudies, getOrganization, getGlobalSharedStudies, adoptStudy, unadoptStudy, deleteStudy, getStudyMetrics, getOrgCourses, deleteCourse, getCourseMetrics, saveStudy, uploadImageToStorage } from '../services/firebase';
import { generateStudyPlan, generateBibleImage } from '../services/geminiService';
import { removeUndefined } from '../utils/objUtils';
import { checkAIQuota, incrementAIQuota } from '../services/aiQuotaService';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from './ui/AppHeader';
import { formatAuthorName } from '../utils/formatters';

interface ContentManagerProps {
  user: UserProfile;
  onLogout: () => void;
  onCreateStudy: () => void;
  onEditStudy: (study: Study) => void;
  onCreateCourse: () => void;
  onEditCourse: (course: Course) => void;
  organization?: Organization | null;
}

export const ContentManager: React.FC<ContentManagerProps> = ({
  user,
  onLogout,
  onCreateStudy,
  onEditStudy,
  onCreateCourse,
  onEditCourse,
  organization: propOrg
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'studies' | 'courses' | 'community'>('studies');
  const [orgStudies, setOrgStudies] = useState<Study[]>([]);
  const [myStudies, setMyStudies] = useState<Study[]>([]);
  const [communityStudies, setCommunityStudies] = useState<Study[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(propOrg || null);
  const [loading, setLoading] = useState(true);

  // Search and Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'recent' | 'oldest'>('recent');
  const [showOnlyMyStudies, setShowOnlyMyStudies] = useState(false);

  // Delete Confirmation State
  const [studyToDelete, setStudyToDelete] = useState<Study | null>(null);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);

  // Preview State
  const [previewStudy, setPreviewStudy] = useState<Study | null>(null);

  // Metrics State
  const [studyMetrics, setStudyMetrics] = useState<Record<string, StudyMetrics>>({});
  const [courseMetrics, setCourseMetrics] = useState<Record<string, CourseMetrics>>({});

  // Pagination State
  const ITEMS_PER_PAGE = 12;
  const [studiesPage, setStudiesPage] = useState(1);
  const [coursesPage, setCoursesPage] = useState(1);
  const [communityPage, setCommunityPage] = useState(1);

  // AI Creation State
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState({ topic: '', title: '', sectionCount: 8 });
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiProgress, setAiProgress] = useState<string>('');
  const [isAiSuccess, setIsAiSuccess] = useState(false);
  const [createdStudyId, setCreatedStudyId] = useState<string | null>(null);

  // Redirect non-admins
  useEffect(() => {
    if (!user.isAdmin) {
      navigate('/dashboard');
    }
  }, [user.isAdmin, navigate]);

  // Sync organization from props
  useEffect(() => {
    if (propOrg) {
      setOrganization(propOrg);
    }
  }, [propOrg]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!user.orgId) return;

        const [orgData, studies, coursesData] = await Promise.all([
          getOrganization(user.orgId),
          getOrgStudies(user.orgId),
          getOrgCourses(user.orgId)
        ]);

        setOrganization(orgData);
        setOrgStudies(studies);
        setCourses(coursesData);

        // Fetch my studies
        const myCreated = await getUserStudies(user.uid);
        setMyStudies(myCreated);

        // Fetch metrics for studies
        const studyMetricsPromises = studies.map(study => getStudyMetrics(study.id));
        const studyMetricsResults = await Promise.all(studyMetricsPromises);
        const studyMetricsMap: Record<string, StudyMetrics> = {};
        studyMetricsResults.forEach((metrics, index) => {
          if (metrics) {
            studyMetricsMap[studies[index].id] = metrics;
          }
        });
        setStudyMetrics(studyMetricsMap);

        // Fetch metrics for courses
        const courseMetricsPromises = coursesData.map(course => getCourseMetrics(course.id));
        const courseMetricsResults = await Promise.all(courseMetricsPromises);
        const courseMetricsMap: Record<string, CourseMetrics> = {};
        courseMetricsResults.forEach((metrics, index) => {
          if (metrics) {
            courseMetricsMap[coursesData[index].id] = metrics;
          }
        });
        setCourseMetrics(courseMetricsMap);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user.uid, user.orgId]);

  // Fetch community studies when tab is active
  useEffect(() => {
    if (activeTab === 'community') {
      const fetchCommunity = async () => {
        setLoading(true);
        try {
          const global = await getGlobalSharedStudies();
          const filtered = global.filter(study => study.orgId !== user.orgId);
          setCommunityStudies(filtered);
        } catch (e) {
          console.error("Failed to load community studies", e);
        } finally {
          setLoading(false);
        }
      };
      fetchCommunity();
    }
  }, [activeTab, user.orgId]);

  const executeDeleteStudy = async () => {
    if (!studyToDelete) return;
    try {
      await deleteStudy(studyToDelete.id);
      setOrgStudies(prev => prev.filter(study => study.id !== studyToDelete.id));
      setMyStudies(prev => prev.filter(study => study.id !== studyToDelete.id));
    } catch (e) {
      console.error("Failed to delete study", e);
      alert("Failed to delete study.");
    } finally {
      setStudyToDelete(null);
    }
  };

  const handleCreateWithAI = async () => {
    if (!aiPrompt.topic || !aiPrompt.title) return alert("Please enter both a topic and a title.");
    if (!user.orgId || !user.uid) return alert("Organization or User ID missing.");

    setIsAiGenerating(true);
    setIsAiSuccess(false);
    setCreatedStudyId(null);
    setAiProgress("Generating study plan...");

    try {
      // 1. Generate Plan (now returns { description, coverImagePrompt, segments })
      const plan = await generateStudyPlan(aiPrompt.topic, aiPrompt.title, aiPrompt.sectionCount);

      const newSegments: any[] = [];
      // Use exact same ID pattern as StudyEditor.tsx for storage path consistency
      const studyId = `study_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // 2. Process Segments & Generate Images
      for (let i = 0; i < plan.segments.length; i++) {
        const planSegment = plan.segments[i];
        setAiProgress(`Processing segment ${i + 1}/${plan.segments.length}...`);

        const segment: any = {
          id: `seg-${Date.now()}-${i}`,
          type: planSegment.type || 'text',
          content: planSegment.content || '',
          metadata: {}
        };

        if (planSegment.type === 'image-text' && planSegment.metadata?.imagePrompt) {
          try {
            setAiProgress(`Generating image for segment ${i + 1}...`);
            const base64Image = await generateBibleImage({
              prompt: planSegment.metadata.imagePrompt,
              style: 'cinematic',
              aspectRatio: '16:9'
            });

            // Upload to storage
            const timestamp = Date.now();
            const storagePath = `orgs/${user.orgId}/studies/${studyId}/segments/${segment.id}-${timestamp}.png`;
            const downloadUrl = await uploadImageToStorage(base64Image, storagePath);

            segment.metadata.imageUrl = downloadUrl;
            segment.metadata.prompt = planSegment.metadata.imagePrompt;
            segment.metadata.uiMode = 'both';

            // Increment quota for segment image
            await incrementAIQuota(user.orgId, user.uid, 'gemini-image', `AI Study Segment: ${segment.id}`, downloadUrl, undefined, undefined, {
              storagePath,
              prompt: planSegment.metadata.imagePrompt,
              sourceType: 'segment',
              associatedStudyId: studyId,
              segmentId: segment.id
            });
          } catch (e) {
            console.error("Failed to generate image for segment", i, e);
            segment.type = 'text';
          }
        }

        newSegments.push(segment);
      }

      // 3. Generate Cover Image
      let coverImageUrl = undefined;
      if (plan.coverImagePrompt) {
        try {
          setAiProgress("Generating cover image...");
          const coverBase64 = await generateBibleImage({
            prompt: plan.coverImagePrompt,
            style: 'cinematic',
            aspectRatio: '16:9'
          });
          const timestamp = Date.now();
          const coverPath = `orgs/${user.orgId}/studies/${studyId}/covers/${timestamp}.png`;
          coverImageUrl = await uploadImageToStorage(coverBase64, coverPath);

          // Increment quota for cover image
          await incrementAIQuota(user.orgId, user.uid, 'gemini-image', 'AI Study Cover', coverImageUrl, undefined, undefined, {
            storagePath: coverPath,
            prompt: plan.coverImagePrompt,
            sourceType: 'cover',
            associatedStudyId: studyId
          });
        } catch (e) {
          console.error("Failed to generate cover image", e);
        }
      }

      // 4. Save Study
      setAiProgress("Saving study...");
      const studyData = removeUndefined({
        orgId: user.orgId,
        authorId: user.uid,
        authorName: user.displayName || 'AI Assistant',
        title: aiPrompt.title,
        description: plan.description || `Study on: ${aiPrompt.topic}`,
        segments: newSegments,
        visibility: VisibilityLevel.PRIVATE, // Default to private as requested
        createdAt: Date.now(),
        tags: [],
        coverImage: coverImageUrl
      });

      const newId = await saveStudy(studyData);
      setCreatedStudyId(newId);
      setIsAiSuccess(true);

      setAiProgress("Refreshing data...");
      const studies = await getOrgStudies(user.orgId);
      setOrgStudies(studies);

    } catch (e) {
      console.error(e);
      alert("Failed to create study with AI. Please try again.");
    } finally {
      setIsAiGenerating(false);
      setAiProgress('');
    }
  };

  const executeDeleteCourse = async () => {
    if (!courseToDelete) return;
    try {
      await deleteCourse(courseToDelete.id);
      setCourses(prev => prev.filter(course => course.id !== courseToDelete.id));
    } catch (e) {
      console.error("Failed to delete course", e);
      alert("Failed to delete course.");
    } finally {
      setCourseToDelete(null);
    }
  };

  const handleAdoptStudy = async (studyId: string) => {
    if (!user.orgId) return;
    try {
      await adoptStudy(user.orgId, studyId, user.uid, user.displayName || 'Unknown User');
      const studies = await getOrgStudies(user.orgId);
      setOrgStudies(studies);
      alert("Study added to your organization's library!");
    } catch (e) {
      console.error(e);
      alert("Failed to adopt study.");
    }
  };

  const handleUnadoptStudy = async (studyId: string) => {
    if (!user.orgId) return;
    try {
      await unadoptStudy(user.orgId, studyId);
      const studies = await getOrgStudies(user.orgId);
      setOrgStudies(studies);
    } catch (e) {
      console.error(e);
      alert("Failed to remove study.");
    }
  };

  // Derived state for filtering
  const baseStudies = showOnlyMyStudies ? myStudies : orgStudies;
  const uniqueTags = Array.from(new Set<string>(baseStudies.flatMap(study => study.tags || []))).sort();

  const filteredStudies = baseStudies
    .filter(study => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        study.title.toLowerCase().includes(query) ||
        study.description.toLowerCase().includes(query) ||
        (study.tags && study.tags.some(t => t.toLowerCase().includes(query)));

      const matchesTags = selectedTags.length === 0 ||
        (study.tags && selectedTags.every(t => study.tags.includes(t)));

      return matchesSearch && matchesTags;
    })
    .sort((a, b) => {
      if (sortBy === 'recent') {
        return b.createdAt - a.createdAt;
      } else {
        return a.createdAt - b.createdAt;
      }
    });

  const filteredCourses = courses
    .filter(course => {
      const query = searchQuery.toLowerCase();
      return course.title.toLowerCase().includes(query) ||
        course.description.toLowerCase().includes(query) ||
        (course.tags && course.tags.some(t => t.toLowerCase().includes(query)));
    })
    .sort((a, b) => {
      if (sortBy === 'recent') {
        return b.createdAt - a.createdAt;
      } else {
        return a.createdAt - b.createdAt;
      }
    });

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
    setStudiesPage(1); // Reset to first page when tag filter changes
  };

  // Reset pages when search/filter changes
  useEffect(() => {
    setStudiesPage(1);
    setCoursesPage(1);
  }, [searchQuery, sortBy, showOnlyMyStudies]);

  // Pagination helpers
  const getPaginatedItems = <T,>(items: T[], page: number): T[] => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const getTotalPages = (totalItems: number): number => {
    return Math.ceil(totalItems / ITEMS_PER_PAGE);
  };

  const renderPagination = (
    currentPage: number,
    totalPages: number,
    setPage: (page: number) => void,
    totalItems: number
  ) => {
    if (totalPages <= 1) return null;

    const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

    return (
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Showing {startItem}-{endItem} of {totalItems} items
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                // Show first, last, current, and adjacent pages
                if (page === 1 || page === totalPages) return true;
                if (Math.abs(page - currentPage) <= 1) return true;
                return false;
              })
              .reduce((acc: (number | string)[], page, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && page - (arr[idx - 1] as number) > 1) {
                  acc.push('...');
                }
                acc.push(page);
                return acc;
              }, [])
              .map((page, idx) => (
                typeof page === 'string' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setPage(page)}
                    className={`w-9 h-9 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === page
                        ? 'bg-primary text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                )
              ))}
          </div>
          <button
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  if (loading && !organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.Loader className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-semibold">Loading content manager...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <AppHeader
        user={user}
        organization={organization}
        onLogout={onLogout}
        activeTab="manage"
        setActiveTab={() => { }}
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icons.Edit className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-gray-900">Content Manager</h1>
          </div>
          <p className="text-gray-500">Create, edit, and manage all studies and courses for {organization?.name}.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('studies')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'studies'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            <div className="flex items-center gap-2">
              <Icons.BookOpen className="w-4 h-4" />
              Studies ({orgStudies.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'courses'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            <div className="flex items-center gap-2">
              <Icons.List className="w-4 h-4" />
              Courses ({courses.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('community')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'community'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            <div className="flex items-center gap-2">
              <Icons.Globe className="w-4 h-4" />
              Community Library
            </div>
          </button>
        </div>

        {/* Studies Tab */}
        {activeTab === 'studies' && (
          <div className="space-y-6 animate-fade-in">
            {/* Actions Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Icons.Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search studies..."
                    className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-sm w-64"
                  />
                </div>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'recent' | 'oldest')}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                >
                  <option value="recent">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>

                {/* My Studies Toggle */}
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={showOnlyMyStudies}
                    onChange={(e) => setShowOnlyMyStudies(e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  My studies only
                </label>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={onCreateStudy}
                  className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg hover:bg-blue-900 transition-all"
                >
                  <Icons.Plus className="w-5 h-5" /> Create Study
                </button>
                <button
                  onClick={() => setShowAIModal(true)}
                  className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg hover:from-indigo-700 hover:to-purple-700 transition-all"
                >
                  <Icons.Sparkles className="w-5 h-5" /> Create with AI
                </button>
              </div>
            </div>

            {/* Tags Filter */}
            {uniqueTags.length > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-gray-500">Filter by tag:</span>
                {uniqueTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all border ${selectedTags.includes(tag)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    #{tag}
                  </button>
                ))}
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="text-xs text-gray-500 hover:text-primary underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Studies Grid */}
            {loading ? (
              <div className="flex justify-center py-20">
                <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredStudies.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                <Icons.BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No studies found.</p>
                <button
                  onClick={onCreateStudy}
                  className="mt-4 text-primary font-medium hover:underline"
                >
                  Create your first study
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getPaginatedItems(filteredStudies, studiesPage).map(study => {
                    const isAuthor = study.authorId === user.uid;
                    const metrics = studyMetrics[study.id];

                    return (
                      <div key={study.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                        {/* Cover */}
                        <div
                          className={`h-32 p-4 flex items-center justify-center relative overflow-hidden ${!study.coverImage ? 'bg-gradient-to-br from-primary to-blue-600' : 'bg-gray-900'}`}
                          style={study.coverImage ? {
                            backgroundImage: `url(${study.coverImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: study.coverImagePosition
                              ? `${study.coverImagePosition.x}% ${study.coverImagePosition.y}%`
                              : 'center'
                          } : undefined}
                        >
                          <div className={`absolute inset-0 ${study.coverImage ? 'bg-black/50' : 'bg-black/10'}`} />
                          <h3 className="text-lg font-serif font-bold text-white relative z-10 text-center line-clamp-2 drop-shadow-md">
                            {study.title}
                          </h3>
                        </div>

                        <div className="p-4">
                          {/* Meta */}
                          <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded">{study.segments.length} segments</span>
                            <span className={`px-2 py-1 rounded ${study.visibility === 'public' ? 'bg-blue-100 text-blue-700' :
                              study.visibility === 'all_orgs' ? 'bg-purple-100 text-purple-700' :
                                study.visibility === 'org_only' ? 'bg-green-100 text-green-700' :
                                  'bg-yellow-100 text-yellow-700'
                              }`}>
                              {study.visibility === 'public' ? 'Public' :
                                study.visibility === 'all_orgs' ? 'All Orgs' :
                                  study.visibility === 'org_only' ? 'Org Only' :
                                    'Private'}
                            </span>
                          </div>

                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{study.description}</p>

                          {/* Metrics */}
                          {metrics && (
                            <div className="flex items-center gap-3 mb-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                              <span className="flex items-center gap-1" title="Views">
                                <Icons.Eye className="w-3.5 h-3.5" />
                                {metrics.views}
                              </span>
                              <span className="flex items-center gap-1" title="Completions">
                                <Icons.Check className="w-3.5 h-3.5" />
                                {metrics.completions}
                              </span>
                              <span className="flex items-center gap-1" title="Unique viewers">
                                <Icons.Users className="w-3.5 h-3.5" />
                                {metrics.uniqueViewers.length}
                              </span>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center justify-between pt-3 border-t">
                            <span className="text-xs text-gray-400">By {formatAuthorName(study.authorName)}</span>
                            <div className="flex items-center gap-2">
                              {isAuthor && (
                                <>
                                  <button
                                    onClick={() => onEditStudy(study)}
                                    className="flex items-center gap-1 text-gray-500 hover:text-primary px-2 py-1 rounded transition-colors text-sm"
                                  >
                                    <Icons.Edit className="w-4 h-4" /> Edit
                                  </button>
                                  <button
                                    onClick={() => setStudyToDelete(study)}
                                    className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                  >
                                    <Icons.Trash className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => navigate(`/study/${study.id}`)}
                                className="text-primary font-medium text-sm hover:underline flex items-center gap-1"
                              >
                                View <Icons.Next className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {renderPagination(studiesPage, getTotalPages(filteredStudies.length), setStudiesPage, filteredStudies.length)}
              </>
            )}
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div className="space-y-6 animate-fade-in">
            {/* Actions Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="relative">
                <Icons.Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search courses..."
                  className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-sm w-64"
                />
              </div>

              <button
                onClick={onCreateCourse}
                className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg hover:bg-blue-900 transition-all"
              >
                <Icons.Plus className="w-5 h-5" /> Create Course
              </button>
            </div>

            {/* Courses Grid */}
            {loading ? (
              <div className="flex justify-center py-20">
                <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                <Icons.List className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No courses found.</p>
                <button
                  onClick={onCreateCourse}
                  className="mt-4 text-primary font-medium hover:underline"
                >
                  Create your first course
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getPaginatedItems(filteredCourses, coursesPage).map(course => {
                    const isAuthor = course.authorId === user.uid;
                    const metrics = courseMetrics[course.id];

                    return (
                      <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                        {/* Cover */}
                        {course.coverImage ? (
                          <div
                            className="h-32 bg-cover bg-center"
                            style={{
                              backgroundImage: `url(${course.coverImage})`,
                              backgroundPosition: `${course.coverImagePosition?.x || 50}% ${course.coverImagePosition?.y || 50}%`
                            }}
                          >
                            <div className="w-full h-full bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                              <h3 className="text-white font-bold text-lg line-clamp-2">{course.title}</h3>
                            </div>
                          </div>
                        ) : (
                          <div className="h-32 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-end p-4">
                            <h3 className="text-white font-bold text-lg line-clamp-2">{course.title}</h3>
                          </div>
                        )}

                        <div className="p-4">
                          {/* Meta */}
                          <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded">{course.studyIds?.length || 0} studies</span>
                            <span className={`px-2 py-1 rounded ${course.visibility === 'public' ? 'bg-blue-100 text-blue-700' :
                              course.visibility === 'all_orgs' ? 'bg-purple-100 text-purple-700' :
                                course.visibility === 'org_only' ? 'bg-green-100 text-green-700' :
                                  'bg-yellow-100 text-yellow-700'
                              }`}>
                              {course.visibility === 'public' ? 'Public' :
                                course.visibility === 'all_orgs' ? 'All Orgs' :
                                  course.visibility === 'org_only' ? 'Org Only' :
                                    'Private'}
                            </span>
                            {course.requireSequentialViewing && (
                              <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded flex items-center gap-1">
                                <Icons.Lock className="w-3 h-3" /> Sequential
                              </span>
                            )}
                          </div>

                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{course.description}</p>

                          {/* Metrics */}
                          {metrics && (
                            <div className="flex items-center gap-3 mb-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                              <span className="flex items-center gap-1" title="Views">
                                <Icons.Eye className="w-3.5 h-3.5" />
                                {metrics.views}
                              </span>
                              <span className="flex items-center gap-1" title="Completions">
                                <Icons.Check className="w-3.5 h-3.5" />
                                {metrics.completions}
                              </span>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center justify-between pt-3 border-t">
                            <span className="text-xs text-gray-400">By {formatAuthorName(course.authorName)}</span>
                            <div className="flex items-center gap-2">
                              {isAuthor && (
                                <>
                                  <button
                                    onClick={() => onEditCourse(course)}
                                    className="flex items-center gap-1 text-gray-500 hover:text-primary px-2 py-1 rounded transition-colors text-sm"
                                  >
                                    <Icons.Edit className="w-4 h-4" /> Edit
                                  </button>
                                  <button
                                    onClick={() => setCourseToDelete(course)}
                                    className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                  >
                                    <Icons.Trash className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => navigate(`/course/${course.id}`)}
                                className="text-primary font-medium text-sm hover:underline flex items-center gap-1"
                              >
                                View <Icons.Next className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {renderPagination(coursesPage, getTotalPages(filteredCourses.length), setCoursesPage, filteredCourses.length)}
              </>
            )}
          </div>
        )}

        {/* Community Tab */}
        {activeTab === 'community' && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-purple-50 p-6 rounded-2xl border border-purple-100">
              <h2 className="text-xl font-serif font-bold text-gray-900 flex items-center gap-2 mb-2">
                <Icons.Globe className="w-5 h-5 text-purple-600" />
                Community Library
              </h2>
              <p className="text-purple-800">Browse and adopt shared studies from other organizations.</p>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : communityStudies.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                <Icons.Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No shared studies available from other organizations yet.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getPaginatedItems(communityStudies, communityPage).map(study => {
                    const isAdopted = orgStudies.some(s => s.clonedFrom === study.id);

                    return (
                      <div key={study.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                        {/* Cover */}
                        <div
                          className={`h-32 p-4 flex items-center justify-center relative overflow-hidden ${!study.coverImage ? 'bg-gradient-to-br from-purple-500 to-pink-600' : 'bg-gray-900'}`}
                          style={study.coverImage ? {
                            backgroundImage: `url(${study.coverImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          } : undefined}
                        >
                          <div className={`absolute inset-0 ${study.coverImage ? 'bg-black/50' : 'bg-black/10'}`} />
                          <h3 className="text-lg font-serif font-bold text-white relative z-10 text-center line-clamp-2 drop-shadow-md">
                            {study.title}
                          </h3>
                        </div>

                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded">{study.segments.length} segments</span>
                            <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
                              <Icons.Globe className="w-3 h-3" /> Shared
                            </span>
                          </div>

                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{study.description}</p>

                          <div className="flex items-center justify-between pt-3 border-t">
                            <span className="text-xs text-gray-400">By {formatAuthorName(study.authorName)}</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setPreviewStudy(study)}
                                className="text-gray-600 font-medium text-sm hover:underline"
                              >
                                Preview
                              </button>
                              {isAdopted ? (
                                <button
                                  onClick={() => handleUnadoptStudy(study.id)}
                                  className="text-red-600 font-medium text-sm hover:underline"
                                >
                                  Remove
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleAdoptStudy(study.id)}
                                  className="bg-primary text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-blue-900"
                                >
                                  Add to Org
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {renderPagination(communityPage, getTotalPages(communityStudies.length), setCommunityPage, communityStudies.length)}
              </>
            )}
          </div>
        )}
      </main>

      {/* Delete Study Confirmation Modal */}
      {studyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Delete Study?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{studyToDelete.title}</strong>"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setStudyToDelete(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={executeDeleteStudy}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Course Confirmation Modal */}
      {courseToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Delete Course?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{courseToDelete.title}</strong>"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setCourseToDelete(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={executeDeleteCourse}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Study Preview Modal */}
      {previewStudy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] shadow-2xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">{previewStudy.title}</h2>
                <p className="text-gray-600 text-sm">{previewStudy.description}</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <span>{previewStudy.segments.length} segments</span>
                  <span>By {formatAuthorName(previewStudy.authorName)}</span>
                </div>
              </div>
              <button onClick={() => setPreviewStudy(null)} className="text-gray-400 hover:text-gray-600">
                <Icons.X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {previewStudy.segments.map((segment, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                        {index + 1}
                      </span>
                      <span className="text-xs font-medium text-gray-500 uppercase">{segment.type}</span>
                    </div>
                    <p className="text-gray-700 text-sm line-clamp-3">{segment.content}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setPreviewStudy(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
              {!orgStudies.some(s => s.clonedFrom === previewStudy.id) && (
                <button
                  onClick={() => {
                    handleAdoptStudy(previewStudy.id);
                    setPreviewStudy(null);
                  }}
                  className="flex-1 px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-blue-900 flex items-center justify-center gap-2"
                >
                  <Icons.Download className="w-4 h-4" /> Add to Organization
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* AI Creation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 text-indigo-600">
                <Icons.Sparkles className="w-6 h-6" />
                <h3 className="text-xl font-serif font-bold text-gray-900">Create Study with AI</h3>
              </div>
              <button
                onClick={() => setShowAIModal(false)}
                disabled={isAiGenerating}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <Icons.X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {isAiSuccess ? (
                <div className="text-center py-6 animate-fade-in">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icons.Check className="w-10 h-10" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Study Created!</h4>
                  <p className="text-gray-600 mb-6">
                    Your study <strong>"{aiPrompt.title}"</strong> has been generated with AI and is ready for review.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        if (createdStudyId) navigate(`/study/${createdStudyId}/edit`);
                      }}
                      className="w-full py-3 bg-primary text-white rounded-lg hover:bg-blue-900 font-bold shadow-md flex items-center justify-center gap-2"
                    >
                      <Icons.Eye className="w-5 h-5" />
                      View & Edit Study
                    </button>
                    <button
                      onClick={() => {
                        setShowAIModal(false);
                        setIsAiSuccess(false);
                        setCreatedStudyId(null);
                        setAiPrompt({ topic: '', title: '', sectionCount: 8 });
                      }}
                      className="w-full py-2 text-gray-500 hover:text-gray-700 font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Study Title</label>
                    <input
                      type="text"
                      value={aiPrompt.title}
                      onChange={(e) => setAiPrompt({ ...aiPrompt, title: e.target.value })}
                      placeholder="e.g., The Parables of Jesus"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      disabled={isAiGenerating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Sections (5-50)</label>
                    <input
                      type="number"
                      min="5"
                      max="50"
                      value={aiPrompt.sectionCount}
                      onChange={(e) => setAiPrompt({ ...aiPrompt, sectionCount: parseInt(e.target.value) || 8 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      disabled={isAiGenerating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Topic / Description</label>
                    <textarea
                      value={aiPrompt.topic}
                      onChange={(e) => setAiPrompt({ ...aiPrompt, topic: e.target.value })}
                      placeholder="e.g., A deep dive into the meaning of the Good Samaritan and the Prodigal Son..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[100px]"
                      disabled={isAiGenerating}
                    />
                  </div>

                  {isAiGenerating && (
                    <div className="bg-indigo-50 text-indigo-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2 animate-pulse">
                      <Icons.Loader className="w-4 h-4 animate-spin" />
                      {aiProgress}
                    </div>
                  )}

                  <button
                    onClick={handleCreateWithAI}
                    disabled={isAiGenerating || !aiPrompt.title || !aiPrompt.topic}
                    className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 font-bold shadow-md transition-all flex items-center justify-center gap-2"
                  >
                    {isAiGenerating ? (
                      <>Building Study...</>
                    ) : (
                      <>
                        <Icons.Sparkles className="w-5 h-5" />
                        Generate Full Study
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
